import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { PickupSlot } from '../../../../model/pickup-slot';
import { Router } from '@angular/router';
import {
    AccountAddress,
    AccountPreferenceByAccountMappingsId200Response,
    AccountPreferences,
    AppointmentSlotData,
    AuctionCatalogsService,
    AvailableSlotsListResponse,
    BookingResponse,
    GetAllAddresses200Response,
    GetAllBookings200Response,
    InvoiceDetail,
    ProfilesService,
    SchedulerWithHoursData,
    UsersService,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';
import { PickupSlotService } from '../../../../services/pickup-slot.service';
import { BiddingUtils } from '@/vb-utils/bidding.util';
import { FeatureFlagUtil } from 'src/app/core/utils/feature-flag.util';
import { FeatureFlag } from 'src/app/core/constants/feature-flag.enum';

@Component({
    selector: 'vb-invoice-fulfillment-details',
    templateUrl: './invoice-fulfillment-details.component.html',
    styleUrls: ['./invoice-fulfillment-details.component.scss'],
})
export class InvoiceFulfillmentDetailsComponent implements OnInit, OnChanges {
    @Input() scheduleLater: boolean = false;
    @Input() isStepOneComplete: boolean = false;
    @Input() isStepTwoComplete: boolean = false;
    @Input() selectedDeliveryMethod: string | null = null;
    @Input() hasPickupScheduled: boolean = false;
    @Output() isStepTwoCompleteChange: EventEmitter<boolean> =
        new EventEmitter<boolean>();
    @Output()
    selectedShippingAddressChange: EventEmitter<AccountAddress | null> =
        new EventEmitter<AccountAddress | null>();
    @Output() selectedPickupSlotChange: EventEmitter<PickupSlot | null> =
        new EventEmitter<PickupSlot | null>();
    @Output() selectedDateChange: EventEmitter<Date> = new EventEmitter<Date>();
    userAddresses: AccountAddress[] = [];
    @Input() selectedShippingAddress: AccountAddress | null = null;
    filteredPickupSlots: PickupSlot[] = [];
    defaultAddresses: string = '';

    @Input() instructions: string = '';
    @Input() invoiceDetails: InvoiceDetail | null = null;
    /** FeatureFlag.PickupFulfillment — the scheduler chosen in step 1. When
     *  set (and the flag is on), available slots come from the scheduler
     *  itself (GET /schedulers/:id/available-slots) instead of the legacy
     *  account-wide getAllBookings lookup, and the final booking books
     *  against this scheduler — see invoice-actions-page's createPickup. */
    @Input() selectedScheduler: SchedulerWithHoursData | null = null;

    @Input() proxyName: string = '';
    @Input() proxyTelephone: string = '';
    bookedSlots: Array<BookingResponse> = [];
    availableSlots: Array<AppointmentSlotData> = [];
    private lastFetchedDate: Date | null = null;
    @Input() selectedDate: Date = new Date();

    morningSlots: PickupSlot[] = [];
    afternoonSlots: PickupSlot[] = [];
    @Input() selectedSlot: PickupSlot | null = null;
    @Output() proxyNameChange: EventEmitter<string> =
        new EventEmitter<string>();
    @Output() proxyTelephoneChange: EventEmitter<string> =
        new EventEmitter<string>();
    @Output() deliveryInstructionsChange: EventEmitter<string> =
        new EventEmitter<string>();
    constructor(
        private router: Router,
        private userService: UsersService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private auctionCatalogsService: AuctionCatalogsService,
        private biddingUtils: BiddingUtils,
        private profileService: ProfilesService,
        private featureFlagUtil: FeatureFlagUtil,
    ) {}

    get pickupFulfillmentEnabled(): boolean {
        return this.featureFlagUtil.isFeatureEnabled(
            FeatureFlag.PickupFulfillment,
        );
    }

    ngOnInit(): void {
        this.biddingUtils
            .getCurrentUserId()
            .subscribe(async (currentUserId: string) => {
                await this.getUserPreferences(currentUserId);
                await this.getAllAddresses();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedDeliveryMethod']) {
            this.selectedDeliveryMethod =
                changes['selectedDeliveryMethod'].currentValue;
            this.isStepTwoComplete = false;
        }
        if (changes['scheduleLater']) {
            this.scheduleLater = changes['scheduleLater'].currentValue;
        }
        if (changes['isStepOneComplete']) {
            this.isStepOneComplete = changes['isStepOneComplete'].currentValue;
            this.isStepTwoComplete = false;
        }
        if (changes['selectedShippingAddress']) {
            this.selectedShippingAddress =
                changes['selectedShippingAddress'].currentValue;
        }
        if (changes['selectedDate']) {
            this.selectedDate = changes['selectedDate'].currentValue;
            this.filterPickupSLots();
        }
        if (changes['selectedSlot']) {
            this.selectedSlot = changes['selectedSlot'].currentValue;
        }
        if (changes['proxyName']) {
            this.proxyName = changes['proxyName'].currentValue;
        }
        if (changes['proxyTelephone']) {
            this.proxyTelephone = changes['proxyTelephone'].currentValue;
        }
        if (changes['instructions']) {
            this.instructions = changes['instructions'].currentValue;
        }
        if (changes['isStepTwoComplete']) {
            this.isStepTwoComplete = changes['isStepTwoComplete'].currentValue;
        }
        if (changes['invoiceDetails']) {
            this.invoiceDetails = changes['invoiceDetails'].currentValue;
            this.proxyTelephone = this.invoiceDetails?.buyer?.telephone
                ? this.invoiceDetails.buyer.telephone.startsWith('+')
                    ? this.invoiceDetails.buyer.telephone.slice(2)
                    : this.invoiceDetails.buyer.telephone
                : '';
        }
        if (changes['hasPickupScheduled']) {
            if (this.hasPickupScheduled) {
                this.isStepTwoCompleteChange.emit();
            }
        }
        if (changes['selectedScheduler']) {
            this.selectedScheduler = changes['selectedScheduler'].currentValue;
            // A scheduler switch invalidates whatever slots were loaded for
            // the previous one — re-fetch for the currently selected date if
            // we've already fetched at least once.
            this.selectedSlot = null;
            if (this.lastFetchedDate) {
                this.fetchSlotsForDate(this.selectedDate);
            }
        }
    }

    getAllAddresses(): void {
        this.userService.getAllAddresses(1, -1).subscribe({
            next: (response: GetAllAddresses200Response) => {
                this.userAddresses = this.sortedAddresses(response.data);
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant('Invoices.FetchAddressError'),
                );
            },
        });
    }

    private sortedAddresses(addresses: AccountAddress[]): AccountAddress[] {
        if (addresses.length === 0) {
            return [];
        }
        return addresses.sort((a: AccountAddress, b: AccountAddress) => {
            return (
                (this.isDefaultAddress(b.id ?? '') ? 1 : 0) -
                (this.isDefaultAddress(a.id ?? '') ? 1 : 0)
            );
        });
    }

    private filterPickupSLots(): void {
        this.filteredPickupSlots =
            this.pickupFulfillmentEnabled && this.selectedScheduler
                ? PickupSlotService.filterAppointmentSlotsByDate(
                      this.availableSlots,
                      this.selectedDate,
                  )
                : PickupSlotService.filterPickupSlotsByDate(
                      this.bookedSlots,
                      this.selectedDate,
                  );
        this.splitSlots();
    }

    handleDateSelect(selectedDate: Date): void {
        this.selectedDate = selectedDate;
        this.selectedSlot = null;
        this.fetchSlotsForDate(selectedDate);
    }

    /** Fetches availability for just the selected day — not the whole
     *  visible month — since that's all the slot list ever renders. */
    private fetchSlotsForDate(date: Date): void {
        this.lastFetchedDate = date;
        const day: string = PickupSlotService.formatDateToYMD(date);

        // FeatureFlag.PickupFulfillment — slots come from the chosen
        // scheduler's own availability, not the account-wide booking feed.
        if (this.pickupFulfillmentEnabled && this.selectedScheduler?.id) {
            this.auctionCatalogsService
                .listPublicSchedulerAvailableSlots(
                    this.selectedScheduler.id,
                    day,
                    day,
                )
                .subscribe({
                    next: (response: AvailableSlotsListResponse) => {
                        this.availableSlots = response.data ?? [];
                        this.filterPickupSLots();
                    },
                    error: () => {
                        this.snackbarService.error(
                            this.translate.instant(
                                'Invoices.fetchPickupSlotsError',
                            ),
                        );
                    },
                });
            return;
        }

        this.auctionCatalogsService.getAllBookings(day, day).subscribe({
            next: (response: GetAllBookings200Response) => {
                this.bookedSlots = response.data;
                this.filterPickupSLots();
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant('Invoices.fetchPickupSlotsError'),
                );
            },
        });
    }

    private splitSlots(): void {
        this.morningSlots = this.filteredPickupSlots.filter(
            (slot: PickupSlot) => slot.zone === 'AM',
        );
        this.afternoonSlots = this.filteredPickupSlots.filter(
            (slot: PickupSlot) => slot.zone === 'PM',
        );
    }

    handleTimeSlotSelect($event: PickupSlot): void {
        if ($event.availableSlots > 0) {
            this.selectedSlot = $event;
        }
    }

    /** Confirm pickup / shipping Next — commits every locally-edited value up
     *  to the parent, then completes the step. */
    onFulfillmentNextClick(): void {
        this.isStepTwoComplete = true;
        this.isStepTwoCompleteChange.emit(this.isStepTwoComplete);
        this.selectedShippingAddressChange.emit(this.selectedShippingAddress);
        this.selectedPickupSlotChange.emit(this.selectedSlot);
        this.proxyNameChange.emit(this.proxyName);
        this.proxyTelephoneChange.emit(this.proxyTelephone);
        this.deliveryInstructionsChange.emit(this.instructions);
        this.selectedDateChange.emit(this.selectedDate);
    }

    goToAddAddress(): void {
        this.router.navigate(['profiles/my-profile/user-addresses']);
    }

    selectAddress(address: AccountAddress): void {
        this.selectedShippingAddress = address;
    }

    isAddressSelected(address: AccountAddress): boolean {
        return (
            !!this.selectedShippingAddress?.id &&
            this.selectedShippingAddress.id === address.id
        );
    }

    /** "214 Sterling Rd, Unit 3" — street + unit on the row's main line. */
    addressLine1(address: AccountAddress): string {
        return [address.address1, address.address2]
            .filter((part?: string) => !!part)
            .join(', ');
    }

    /** "Toronto, ON  M6R 2B2" — the supporting line. */
    addressLine2(address: AccountAddress): string {
        const cityProvince: string = [address.city, address.province]
            .filter((part?: string) => !!part)
            .join(', ');
        return [cityProvince, address.postalCode]
            .filter((part?: string) => !!part)
            .join('  ');
    }

    isDefaultAddress(addressId?: string): boolean {
        if (!addressId) return false;
        if (this.defaultAddresses) {
            return this.defaultAddresses === addressId;
        } else return false;
    }

    async getUserPreferences(userId: string): Promise<void> {
        if (userId) {
            await this.profileService
                .accountPreferenceByAccountMappingsId(userId)
                .subscribe({
                    next: (
                        response: AccountPreferenceByAccountMappingsId200Response,
                    ) => {
                        const preferenceResponse: AccountPreferences =
                            response.data as AccountPreferences;
                        this.defaultAddresses =
                            preferenceResponse.preferences?.defaultAddressId ??
                            '';
                    },
                    error: () => {
                        this.snackbarService.error(
                            this.translate.instant(
                                'Invoices.FetchUserPreferenceError',
                            ),
                        );
                    },
                });
        }
    }

    allowPhoneInput(event: KeyboardEvent): void {
        const allowedChars: RegExp = /[0-9\-()]/;
        if (
            !allowedChars.test(event.key) &&
            event.key !== 'Backspace' &&
            event.key !== 'Tab' &&
            event.key !== 'ArrowLeft' &&
            event.key !== 'ArrowRight'
        ) {
            event.preventDefault();
        }
    }

    isProxyPhoneValid(): boolean {
        // Allows optional +1, spaces, dashes, parentheses
        const pattern: RegExp = /^(\(\d{3}\)|\d{3})[- ]?\d{3}[- ]?\d{4}$/;
        return pattern.test(this.proxyTelephone);
    }
}
